import { useAuthStore } from '@/stores/authStore';

// 에러 메시지 추출 헬퍼
export const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'object' && error !== null) {
        try {
            return JSON.stringify(error);
        } catch {
            return '[Error object cannot be stringified]';
        }
    }
    return typeof error === 'string' ? error : '[Unknown error]';
};

// 코드 블록 포맷팅 제거
export function removeCodeBlockFormatting(text: string): string {
    if (!text) return text;
    // 코드 블록을 찾는 정규식
    const codeBlockRegex = /^```[\w-]*\n([\s\S]*?)\n```$/;

    // exec 메서드 사용 (match 대신)
    const match = codeBlockRegex.exec(text);

    // 옵셔널 체이닝 사용
    if (match?.[1]) {
        // 일치하면 코드 블록 내부 내용 반환
        return match[1].trim();
    } else {
        // 일치하지 않으면 원본 텍스트 반환
        return text;
    }
}

// writer_node의 content만 추출
export const extractWriterNodeContent = (aiResponse: string): string => {
    try {
        if (
            !aiResponse.includes('data:') &&
            !aiResponse.includes('writer_node')
        ) {
            return aiResponse;
        }
        let extractedContent = '';
        const processedNodes = new Set<string>();
        const dataRegex = /data:\s*({.*?})/g;

        // 문자열을 반복적으로 검색하여 정규식 매치
        let match: RegExpExecArray | null;
        const text = aiResponse; // let -> const로 변경

        // RegExp.exec 메서드 사용하고 타입 안전성 확보
        while ((match = dataRegex.exec(text)) !== null) {
            try {
                if (match[1]) {
                    // null 체크 추가
                    const jsonStr = match[1];
                    // 타입 안전성 확보
                    try {
                        const data = JSON.parse(jsonStr) as Record<
                            string,
                            unknown
                        >;
                        if (
                            data &&
                            typeof data === 'object' &&
                            data.node === 'writer_node' &&
                            typeof data.content === 'string'
                        ) {
                            if (!processedNodes.has(data.content)) {
                                extractedContent += data.content;
                                processedNodes.add(data.content);
                            }
                        }
                    } catch (parseError) {
                        console.warn('[Utils] JSON 파싱 오류:', parseError);
                    }
                }
            } catch (e) {
                console.warn('[Utils] 정규식 매치 처리 오류:', e);
            }
        }

        if (!extractedContent && aiResponse) {
            return aiResponse;
        }
        return extractedContent;
    } catch (err: unknown) {
        console.error(
            '[Utils] writer_node content 추출 오류:',
            getErrorMessage(err)
        );
        return aiResponse;
    }
};

// 이벤트 생성 헬퍼 함수
export const createWriterNodeContentEvent = (
    content: string
): CustomEvent<{ content: string; selectedText?: string | null }> => {
    return new CustomEvent('writer-node-content', {
        detail: { content, selectedText: null },
        bubbles: true,
        cancelable: true,
        composed: true,
    });
};

// 로컬 스토리지에 데이터 저장
export const saveToLocalStorage = <T>(key: string, data: T): void => {
    try {
        localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
        console.error(
            '[Utils] 로컬 스토리지 저장 오류:',
            getErrorMessage(error)
        );
    }
};

// 로컬 스토리지에서 데이터 로드
export const loadFromLocalStorage = <T>(key: string, defaultValue: T): T => {
    try {
        const stored = localStorage.getItem(key);
        if (!stored) return defaultValue;
        return JSON.parse(stored) as T;
    } catch (error) {
        console.error(
            '[Utils] 로컬 스토리지 로드 오류:',
            getErrorMessage(error)
        );
        return defaultValue;
    }
};

// 디바운스 함수 타입
export interface DebouncedFunction<T extends (...args: unknown[]) => unknown> {
    (...args: Parameters<T>): ReturnType<T>;
    cancel: () => void;
}

// 디바운스 유틸리티
export const debounceFunction = <T extends (...args: unknown[]) => unknown>(
    func: T,
    wait = 300
): DebouncedFunction<T> => {
    let timeout: NodeJS.Timeout | null = null;

    // 'this' 사용 안함으로 변경
    const debouncedFn = ((...args: Parameters<T>): ReturnType<T> => {
        const later = function () {
            timeout = null;
            return func(...args);
        };

        if (timeout !== null) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(later, wait);
        return undefined as unknown as ReturnType<T>;
    }) as DebouncedFunction<T>;

    debouncedFn.cancel = () => {
        if (timeout !== null) {
            clearTimeout(timeout);
        }
    };

    return debouncedFn;
};

// 마크다운 처리 유틸리티
export const formatMarkdown = (markdown: string): string => {
    // 기본 마크다운 줄바꿈 처리
    return markdown.replace(/\n/g, '<br />');
};

// Typewriter 관련 인터페이스 추가
export interface TypewriterClass {
    typeString: (text: string) => TypewriterClass;
    pauseFor: (ms: number) => TypewriterClass;
    deleteAll: () => TypewriterClass;
    callFunction: (callback: () => void) => TypewriterClass;
    start: () => TypewriterClass;
}

export interface TypewriterOptions {
    autoStart?: boolean;
    loop?: boolean;
    delay?: number;
    deleteSpeed?: number;
    strings?: string[];
    cursor?: string;
}

// 타입라이터 렌더링 헬퍼 함수 추가
export const createTypewriterProps = (
    content: string,
    onComplete?: () => void,
    options?: Partial<TypewriterOptions>
) => {
    return {
        onInit: (typewriter: TypewriterClass) => {
            typewriter
                .typeString(content)
                .callFunction(() => {
                    if (onComplete) onComplete();
                })
                .start();
        },
        options: {
            delay: 30,
            cursor: '|',
            ...options,
        },
    };
};

export interface EditorMention {
    userId: string; // 멘션된 사람 Id
    userName: string; // 멘션된 사람 이름
    mentionerId: string; // 멘션한 사람 즉 현재 로그인한 사용자의 id
    noteBlockId: string; // 텍스트 블록의 id
    mentionText: string; // 멘션 뒤에 오는 텍스트 - 바로 업데이트 되어서 빈문자열이 들어가는중..
}

/**
 * 노트 콘텐츠에서 멘션을 추출하는 함수
 * @param content 노트 콘텐츠 JSON 문자열
 * @returns 멘션된 사용자 목록 (userId, userName, mentionerId-현재 로그인된 유저, noteBlockId-멘션된 블록 ID, mentionText-멘션 뒤에 오는 텍스트)
 */
export function extractMentionsFromNoteContent(
    content: string
): EditorMention[] {
    try {
        // 빈 문자열이나 유효하지 않은 입력 체크
        if (!content || content.trim() === '') {
            return [];
        }

        const { user } = useAuthStore.getState();

        // 콘텐츠 파싱
        const blocks = JSON.parse(content) as {
            id: string;
            content: {
                type: string;
                props?: { id: string; name: string };
                text?: string;
                styles?: Record<string, unknown>;
            }[];
        }[];
        const mentions: EditorMention[] = [];

        // 모든 블록 순회
        for (const block of blocks) {
            // 블록에 content 배열이 있는 경우에만 처리
            if (Array.isArray(block.content)) {
                // content 배열 내 멘션 항목 찾기
                for (let i = 0; i < block.content.length; i++) {
                    const item = block.content[i];
                    // type이 'mention'인 항목 찾기
                    if (item.type === 'mention' && item.props) {
                        // 멘션 뒤에 오는 텍스트 확인 (있으면 가져오고, 없으면 빈 문자열)
                        let followingText = '';

                        // 다음 항목이 존재하고 텍스트 타입인 경우
                        if (
                            i + 1 < block.content.length &&
                            block.content[i + 1].type === 'text'
                        ) {
                            followingText = block.content[i + 1].text || '';
                        }

                        mentions.push({
                            userId: item.props.id,
                            userName: item.props.name,
                            mentionerId: user.id,
                            noteBlockId: block.id,
                            mentionText: followingText,
                        });
                    }
                }
            }
        }

        return mentions;
    } catch (error) {
        console.error('멘션 추출 중 오류 발생:', error);
        return [];
    }
}

/**
 * 두 멘션을 비교하여 동일한 멘션인지 확인하는 함수
 */
function areMentionsEqual(
    mention1: EditorMention,
    mention2: EditorMention
): boolean {
    return (
        mention1.userId === mention2.userId &&
        mention1.mentionerId === mention2.mentionerId &&
        mention1.noteBlockId === mention2.noteBlockId
    );
}

/**
 * 멘션 리스트에 동일한 멘션이 존재하는지 확인하는 함수
 * @param mention
 * @param mentionArray
 * @returns
 */
export function findMentionInArray(
    mention: EditorMention,
    mentionArray: EditorMention[]
): boolean {
    return mentionArray.some(item => areMentionsEqual(item, mention));
}

export default {
    getErrorMessage,
    removeCodeBlockFormatting,
    extractWriterNodeContent,
    createWriterNodeContentEvent,
    saveToLocalStorage,
    loadFromLocalStorage,
    debounceFunction,
    formatMarkdown,
    createTypewriterProps,
    extractMentionsFromNoteContent,
    findMentionInArray,
};
